import type { ReactNode } from "react";

export function Section({
  eyebrow,
  title,
  subtitle,
  children,
  className = "",
  /**
   * Heading level for `title`. Pages whose hero is a `Section` pass `1` so the
   * document has exactly one H1; everything else stays an H2. Styling is
   * identical either way — only the tag changes.
   */
  headingLevel = 2,
  id,
}: {
  id?: string;
  eyebrow?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  className?: string;
  headingLevel?: 1 | 2;
}) {
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const padding = sectionPadding(className);

  return (
    <section
      id={id}
      className={`${padding} ${className}`.trim()}
      // Skip rendering work for sections far offscreen — big paint/layout win on long pages.
      // `auto` lets the browser remember each section's real height after its
      // first render, so the scrollbar and anchor offsets stop jumping when a
      // section is taller or shorter than the 1200px placeholder.
      style={{ scrollMarginTop: id ? "6rem" : undefined, contentVisibility: "auto", containIntrinsicSize: "auto 1200px" }}
    >
      <div className="container-x">
        {(eyebrow || title) && (
          <div className="max-w-3xl mb-16">
            {eyebrow && (
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary mb-5">
                <span className="size-1.5 rounded-full bg-primary shadow-[0_0_12px_var(--glow)]" />
                {eyebrow}
              </div>
            )}
            {title && (
              <Heading className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                {title}
              </Heading>
            )}
            {subtitle && (
              <p className="mt-5 text-base md:text-lg text-muted-foreground">{subtitle}</p>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

/**
 * Default vertical rhythm, dropped per side when the caller sets its own
 * top/bottom padding. Without this, the default `lg:py-32` (a media-query
 * rule) silently beat callers' plain `pt-8` / `pb-16` overrides on desktop.
 */
function sectionPadding(className: string): string {
  const top = /(^|\s)([\w-]+:)?!?(pt|py|p)-/.test(className);
  const bottom = /(^|\s)([\w-]+:)?!?(pb|py|p)-/.test(className);
  return [top ? "" : "pt-24 lg:pt-32", bottom ? "" : "pb-24 lg:pb-32"].filter(Boolean).join(" ");
}
